import { defaultChatbotId, defaultUuid } from '@/router'
import { storageHelper } from '@/utils'
import { defineStore } from 'pinia'
import { xaiModels } from './xai-model.enum'
import type {
  ChatbotData,
  ChatbotHistory,
  ChatbotMessage,
  IChatbotState,
} from './chatbot.types'
import type { XaiModelType } from './provider.model-types'

enum ChatbotStorageKeyEnum {
  activeId = '$xai@activeChatid',
  models = '$xai@models',
  history = '$xai@history',
  chat = '$xai@chats',
}

export const useBotStore = defineStore('bot', {
  state: (): IChatbotState => ({ ...initLoadLocaleState() }),
  getters: {
    currentChatid: (state) => state.activeId ?? defaultChatbotId,
    currentHistory: (state): ChatbotHistory => {
      const chatid = state.activeId ?? defaultChatbotId
      let history = state.history.find((it) => it.chatid === chatid)
      const model = getDefaultModel(state.models)
      if (!history) {
        history = createNewHistory(model, chatid, defaultUuid)
      }

      return history
    },
    currentChat: (state): ChatbotData => {
      const chatid = state.activeId ?? defaultChatbotId
      let chat = state.chat.find((c) => c.chatid === chatid)
      if (!chat) {
        chat = {
          chatid,
          data: [],
        } as ChatbotData
      }

      return chat
    },
    modelSelections: (state): XaiModelType[] => {
      const { activeId, models, history } = state
      if (!history?.length)
        return models?.map((m, idx) => ({ ...m, selected: idx === 0 }))

      const his = history.find((h) => h.chatid === activeId)
      if (!his) return models?.map((m, idx) => ({ ...m, selected: idx === 0 }))

      return models.map((m) => ({ ...m, selected: m.modelId === his.modelId }))
    },
    currentModelName: (state): string => {
      const { models, activeId, history } = state

      const his = history.find((h) => h.chatid === activeId)
      if (!his) return ''
      return models.find((m) => m.modelId === his.modelId)?.modelName ?? ''
    },
    usingContext: (state): boolean => {
      const { activeId, history } = state
      const his = history.find((h) => h.chatid === activeId)
      return !!his?.usingContext
    },
  },
  actions: {
    /**
     * 更新 ChatbotData
     */
    updateChat(chat: ChatbotData) {
      const idx = this.$state.chat.findIndex((c) => c.chatid === chat.chatid)

      if (idx < 0) {
        this.$state.chat.unshift(chat)
      } else {
        this.$state.chat.splice(idx, 1, chat)
      }
      presistentChat(this.$state.chat)
    },
    /**
     * 切換model
     * @param modelInfo
     * @param activeid
     * @returns ChatbotHistory
     */
    switchModel(modelInfo: XaiModelType, activeid: string): ChatbotHistory {
      const currIdx = this.$state.history.findIndex(
        (h) => h.chatid === activeid,
      )
      if (currIdx < 0) {
        const currHis = createNewHistory(modelInfo, activeid, defaultUuid)
        this.$state.history.unshift(currHis)
        return currHis
      } else {
        const { modelId, model, provider, modelName } = modelInfo
        const currHis = this.$state.history[currIdx]
        this.$state.history.splice(currIdx, 1, {
          ...currHis,
          modelId,
          model,
          provider,
          modelName,
        })
        return { ...currHis, modelId, model, provider, modelName }
      }
    },
    async appendChatbotMessage(
      message: ChatbotMessage,
      chatid: string,
    ): Promise<boolean> {
      const chatDataIdx = this.$state.chat.findIndex((c) => c.chatid === chatid)
      if (chatDataIdx < 0) {
        return false
      }
      const chat = this.$state.chat[chatDataIdx]
      if (!chat.data) {
        chat.data = []
      }
      await chat.data.push(message)
      await this.$state.chat.splice(chatDataIdx, 1, chat)
      await presistentChat(this.$state.chat)
      return true
    },
    /**
     *
     * @param some
     * @param chatid
     * @param msgid
     * @returns boolean
     */
    async updateChatDataMessage(
      some: Partial<
        Omit<
          ChatbotMessage,
          'msgid' | 'chatid' | 'created' | 'role' | 'inversion'
        >
      >,
      chatid: string,
      msgid: string,
    ): Promise<boolean> {
      const chatDataIdx = this.$state.chat.findIndex((c) => c.chatid === chatid)
      if (chatDataIdx < 0) {
        return false
      }

      const chat = this.$state.chat[chatDataIdx]
      if (!chat?.data?.length) {
        return false
      }
      const msgIdx = chat.data.findIndex((d) => d.msgid === msgid)
      if (msgIdx < 0) {
        return false
      }
      chat.data.splice(msgIdx, 1, { ...chat.data[msgIdx], ...some })
      await this.$state.chat.splice(chatDataIdx, 1, chat)

      await presistentChat(this.$state.chat)
      return true
    },
    cleanData() {
      const activeId = this.$state.activeId ?? defaultChatbotId
      clearHistoryData(activeId)
    },
    saveAll() {
      const { activeId, models, history, chat } = this.$state
      presistentActiveId(activeId ?? defaultChatbotId)
      presistentModels(models)
      presistentHistory(history)
      presistentChat(chat)
    },
  },
})

// 緩存時間
const storageExpires = 60 * 60 * 24 * 3

function initLoadLocaleState(): IChatbotState {
  let chatid = storageHelper.get(ChatbotStorageKeyEnum.activeId)
  if (!chatid?.length) {
    chatid = defaultChatbotId
    presistentActiveId(chatid)
  }

  let models = storageHelper.get(ChatbotStorageKeyEnum.models)
  if (!models?.length) {
    models = [...xaiModels]
    presistentModels(models)
  }

  const defModel = getDefaultModel(models)

  const h = createNewHistory(defModel, chatid, defaultUuid)
  let history = storageHelper.get(ChatbotStorageKeyEnum.history)
  if (!history?.length) {
    history = []

    // insert head
    history.unshift(h)
    // (history as any as ChatbotHistory[]).
  } else if (!(history as ChatbotHistory[]).find((h) => h.chatid === chatid)) {
    // insert head
    history.unshift(h)
  }

  let chat = storageHelper.get(ChatbotStorageKeyEnum.chat)
  if (!chat?.length) {
    chat = [createNewChatbotData(chatid)]
  } else if (!(chat as any as ChatbotData[]).find((c) => c.chatid === chatid)) {
    chat.unshift(createNewChatbotData(chatid))
  }

  const localeState: IChatbotState = {
    activeId: chatid,
    models,
    history,
    chat,
  }

  return localeState
}

function getDefaultModel(models: XaiModelType[]): XaiModelType {
  if (!models?.length) return xaiModels[0]
  let m = models.filter((m) => !m.disabled)?.[0]
  if (!m) m = xaiModels[0]
  return m
}

function createNewHistory(
  modelInfo: XaiModelType,
  chatid: string,
  uuid: number,
): ChatbotHistory {
  const { modelId, model, provider, modelName } = modelInfo
  const history: ChatbotHistory = {
    chatid,
    uuid,
    title: modelName ?? chatid,
    created: Date.now(),
    modelId,
    provider,
    model,
    usingContext: false,
  }

  return history
}

function createNewChatbotData(chatid: string): ChatbotData {
  return {
    chatid,
    data: [],
  } as ChatbotData
}

function presistentActiveId(chatid: string = defaultChatbotId) {
  storageHelper.set(ChatbotStorageKeyEnum.activeId, chatid, storageExpires)
}

function presistentModels(models: XaiModelType[]) {
  storageHelper.set(ChatbotStorageKeyEnum.models, models, storageExpires)
}

function presistentHistory(histories: ChatbotHistory[]) {
  storageHelper.set(ChatbotStorageKeyEnum.history, histories, storageExpires)
}

function presistentChat(chats: ChatbotData[]) {
  storageHelper.set(ChatbotStorageKeyEnum.chat, chats, storageExpires)
}

function clearHistoryData(activeId: string) {
  const chatids: string[] = [activeId, defaultChatbotId]

  const histories = localeHistories()
  if (histories?.length) {
    const cleanHistories = histories.filter((h) => chatids.includes(h.chatid))
    presistentHistory(cleanHistories)
  }

  const chats = localeChats()
  if (chats?.length) {
    const cleanChats = chats.filter((c) => chatids.includes(c.chatid))
    presistentChat(cleanChats)
  }
}

function localeHistories(): ChatbotHistory[] | null {
  const data = storageHelper.get(ChatbotStorageKeyEnum.history)
  return data ? (data as any as ChatbotHistory[]) : null
}

function localeChats(): ChatbotData[] | null {
  const datas = storageHelper.get(ChatbotStorageKeyEnum.chat)

  return datas ? (datas as any as ChatbotData[]) : null
}
